import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { retryAfterSeconds, waitMessage } from '../utils/rateLimit';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  // Set when the session check got no answer about the session at all (the rate limiter,
  // a 5xx, the network): { rateLimited, retryAfter } with retryAfter in seconds or null.
  // The visitor is then neither signed in nor signed out, and the route guards show a
  // "can't reach the portal" screen instead of sending a live session to /login.
  const [authUnavailable, setAuthUnavailable] = useState(null);

  // Ask the server whether we are signed in: on mount, and again from that screen's
  // Try again button. Never on a timer - each automatic retry spends rate-limit budget
  // the visitor needs once the window resets and adds load to a server that may be
  // struggling, so the visitor decides when to try again.
  //
  // The session lives in an httpOnly cookie, so the client cannot inspect it - there is
  // no token in localStorage to gate on any more. The only way to know whether we are
  // signed in is to ask the server, so this always calls the profile endpoint and lets
  // a 401 mean "logged out".
  const checkAuth = useCallback(async () => {
    try {
      // The route guards explain a 429 here with a full page, so no toast on top.
      const response = await api.get('/api/auth/profile', { explainsOwnRateLimit: true });
      const userData = response.data.data;
      setUser(userData);

      // Set roles and permissions from the profile response
      setUserRoles(userData.roles || []);
      setUserPermissions(userData.permissions || []);
      setAuthUnavailable(null);
    } catch (error) {
      const status = error.response?.status;
      // 401 here is the normal "not signed in" path on a cold load, not a failure.
      if (status !== 401) {
        console.error('🔐 AuthContext: Auth check failed:', error);
      }
      // Rate limited, a server fault, or no response at all: none of these says anything
      // about the session, so they must not be read as "signed out".
      if (!error.response || status === 429 || status >= 500) {
        setAuthUnavailable({ rateLimited: status === 429, retryAfter: retryAfterSeconds(error) });
      } else {
        setUser(null);
        setUserRoles([]);
        setUserPermissions([]);
        setAuthUnavailable(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Seed auth state from a login response. Shared by the password and Google paths so
  // the two cannot diverge on what gets read out of the body - every permission gate
  // in the app is driven by these three pieces of state.
  const applyLoginResponse = (response) => {
    // No token in the body by design - the server sets it as an httpOnly cookie.
    const { user: userData } = response.data.data;

    setUser(userData);
    setUserRoles(userData.roles || []);
    setUserPermissions(userData.permissions || []);
    // /login stays reachable after a session check that got no answer, and a sign-in is
    // that answer: left set, the route guards would show "can't reach the portal" to a
    // user who has just signed in.
    setAuthUnavailable(null);
    return { success: true };
  };

  const reportLoginError = (error, fallback) => {
    console.error('🔐 AuthContext: Login error details:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.response?.data?.message,
      fullError: error
    });

    const message = error.response?.data?.message || fallback;
    toast.error(message);
    return { success: false, error: message };
  };

  const login = async (username, password) => {
    try {
      const response = await api.post('/api/auth/login', {
        username,
        password
      });
      return applyLoginResponse(response);
    } catch (error) {
      return reportLoginError(error, 'Login failed');
    }
  };

  // Exchange a Google Identity Services ID token for a session. The credential is
  // opaque to us: the server verifies it and refuses any address that has no HRMS
  // account, so a 401 here is an authorization answer, not a broken token.
  const loginWithGoogle = async (credential) => {
    try {
      const response = await api.post('/api/auth/google', { credential });
      return applyLoginResponse(response);
    } catch (error) {
      return reportLoginError(error, 'Google sign-in failed');
    }
  };

  const logout = async () => {
    // The cookie is httpOnly, so only the server can clear it. This call also bumps
    // token_version, which is what actually revokes the session - clearing client state
    // alone used to leave the JWT valid server-side for its full 24h.
    try {
      // No body: the instance sends Content-Type: application/json, so a `null` body goes
      // out as the text "null", which the server's strict JSON parser rejects before the
      // route runs - and every logout failed.
      await api.post('/api/auth/logout', undefined, { explainsOwnRateLimit: true });
    } catch (error) {
      // A 401 means the session was already dead, which is the desired end state. Any
      // other failure means the session may still be live, so the user stays signed in
      // and is told so: "Logged out" on a shared computer whose session still works is
      // worse than no logout at all.
      if (error.response?.status !== 401) {
        console.error('🔐 AuthContext: logout request failed:', error);
        const retry = error.response?.status === 429 ? waitMessage(retryAfterSeconds(error)) : 'Please try again.';
        toast.error(`Couldn't sign you out. ${retry}`, { id: 'logout-failed' });
        return;
      }
    }
    setUser(null);
    setUserRoles([]);
    setUserPermissions([]);
    toast.success('Logged out successfully');
  };

  const register = async (userData) => {
    try {
      await api.post('/api/auth/register', userData);
      toast.success('Registration successful! Please login.');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Stable across renders, so an effect can depend on it: OnboardingForm calls it from
  // one, and a new identity per provider render would re-run that effect - and its
  // request - after every refresh.
  const refreshProfile = useCallback(async () => {
    try {
      const response = await api.get('/api/auth/profile');
      const userData = response.data.data;
      setUser(userData);
      setUserRoles(userData.roles || []);
      setUserPermissions(userData.permissions || []);
      return userData;
    } catch (error) {
      console.error('🔐 AuthContext: refreshProfile failed:', error);
      return null;
    }
  }, []);

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await api.put('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      // Refresh profile so must_change_password flips to false in the UI.
      await refreshProfile();
      toast.success('Password changed successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      const details = error.response?.data?.errors?.[0]?.msg;
      toast.error(details || message);
      return { success: false, error: details || message };
    }
  };

  // Permission checking functions
  const hasPermission = (resource, action) => {
    // Check if user has the specific permission
    return userPermissions.some(permission => 
      permission.resource === resource && permission.action === action
    );
  };

  // Helper to check for any update permission (update_all or update_own)
  const hasUpdatePermission = (resource) => {
    return hasPermission(resource, 'update_all') || hasPermission(resource, 'update_own');
  };

  // Helper to check for any read permission (read_all, read_department, or read_own)
  const hasReadPermission = (resource) => {
    return hasPermission(resource, 'read_all') || 
           hasPermission(resource, 'read_department') || 
           hasPermission(resource, 'read_own');
  };

  // Helper to check for any delete permission (delete or delete_all)
  const hasDeletePermission = (resource) => {
    return hasPermission(resource, 'delete') || hasPermission(resource, 'delete_all');
  };

  const hasAnyPermission = (permissions) => {
    // Check if user has any of the specified permissions
    return permissions.some(({ resource, action }) => 
      hasPermission(resource, action)
    );
  };

  const hasRole = (roleName) => {
    // Check if user has the specified role
    return userRoles.some(role => role.name === roleName);
  };

  const hasAnyRole = (roleNames) => {
    // Check if user has any of the specified roles
    return userRoles.some(role => roleNames.includes(role.name));
  };

  // Role-based access control functions
  const isAdmin = () => {
    return hasRole('super_admin') || hasRole('hr_admin');
  };

  const isHR = () => {
    return hasRole('hr_admin') || hasRole('hr_manager');
  };

  const isManager = () => {
    return hasRole('department_manager') || hasRole('hr_manager');
  };

  const isEmployee = () => {
    return hasRole('employee');
  };

  const isFinanceManager = () => {
    return hasRole('finance_manager');
  };

  const isReportViewer = () => {
    return hasRole('report_viewer');
  };

  // Granular permission helpers
  const canViewAllEmployees = () => {
    return hasPermission('employees', 'read_all');
  };

  const canViewDepartmentEmployees = () => {
    return hasPermission('employees', 'read_department');
  };

  const canViewOwnEmployeeData = () => {
    return hasPermission('employees', 'read_own');
  };

  const canViewAllSalaries = () => {
    return hasPermission('salaries', 'read_all');
  };

  const canViewOwnSalary = () => {
    return hasPermission('salaries', 'read_own');
  };

  const canViewAllAppraisals = () => {
    return hasPermission('appraisals', 'read_all');
  };

  const canViewOwnAppraisals = () => {
    return hasPermission('appraisals', 'read_own');
  };

  const canViewReportsAppraisals = () => {
    return hasPermission('appraisals', 'read_reports');
  };

  const canViewAllDocuments = () => {
    return hasPermission('documents', 'read_all');
  };

  const canViewOwnDocuments = () => {
    return hasPermission('documents', 'read_own');
  };

  const canViewSharedDocuments = () => {
    return hasPermission('documents', 'read_shared');
  };

  // Helper: does user have any permission for a resource (section)?
  const hasAnyResourcePermission = (resource) => {
    return userPermissions.some(p => p.resource === resource);
  };

  // Helper: does user have any of a list of actions for a resource?
  const hasAnyResourceAction = (resource, actions) => {
    return userPermissions.some(p => p.resource === resource && actions.includes(p.action));
  };

  const value = {
    user,
    // The session token is an httpOnly cookie and is intentionally not readable here.
    // Authentication state is whether the server gave us a profile.
    isAuthenticated: !!user,
    loading,
    authUnavailable,
    retryAuth: checkAuth,
    userRoles,
    userPermissions,
    login,
    loginWithGoogle,
    logout,
    register,
    changePassword,
    refreshProfile,
    hasPermission,
    hasUpdatePermission,
    hasReadPermission,
    hasDeletePermission,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
    isAdmin,
    isHR,
    isManager,
    isEmployee,
    isFinanceManager,
    isReportViewer,
    // Granular permission helpers
    canViewAllEmployees,
    canViewDepartmentEmployees,
    canViewOwnEmployeeData,
    canViewAllSalaries,
    canViewOwnSalary,
    canViewAllAppraisals,
    canViewOwnAppraisals,
    canViewReportsAppraisals,
    canViewAllDocuments,
    canViewOwnDocuments,
    canViewSharedDocuments,
    // Section permission helpers
    hasAnyResourcePermission,
    hasAnyResourceAction
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 